import React from 'react';
import { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from "../../utils/ContextProvider";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import API from "../../utils/API";
import Swal from "sweetalert2";
import GraphFuncs from '../../utils/GraphFuncs';
import GlobalSVG from '../../utils/GlobalSVG';
import './style.css'
import GlobalFuncs from '../../utils/GlobalFuncs';
import DateRangePicker from '../../components/DateRangePicker';
import ReportGraph1 from '../../components/ReportGraph1';
import ReportPie1 from '../../components/ReportPie1';
import ReportPie2 from '../../components/ReportPie2';
import Accordion from "../../components/Accordion";
import BreakerGraph from '../../components/BreakerGraph';
import ReportGraph2 from '../../components/ReportGraph2';
import Loader from '../../components/Loader';
import moment from 'moment';

// ---------------------------------------------------------------------------
// Helpers (pure - no React state). Pulled out of the component so the two
// data-fetching effects (primary period + compare period) can share the same
// price-allocation / filtering / peak-lookup logic instead of duplicating it.
// ---------------------------------------------------------------------------

const UNDEFINED_LABEL = "Undefined"
const GENERIC_ERROR = {
    title: 'Error.',
    text: 'Please try again later.',
    icon: 'warning',
    confirmButtonText: 'Ok',
    confirmButtonColor: '#007b4e',
}

const isCancel = (e) => e && e.message === 'cancelling'

const showGenericError = () => Swal.fire(GENERIC_ERROR)

// category / sub-category / equipment group / distribution for a circuit, with the
// same "Undefined" fallback the original inline code used when no channel matched.
const extractChannelMeta = (channelInfo) => {
    if (!channelInfo || channelInfo.length === 0) {
        return { category: UNDEFINED_LABEL, subcategory: UNDEFINED_LABEL, equipmentGroup: UNDEFINED_LABEL, distribution: UNDEFINED_LABEL }
    }
    return {
        category: channelInfo[0].category,
        subcategory: channelInfo[0].sub_category,
        equipmentGroup: channelInfo[0].equipment_group,
        distribution: channelInfo[0].building_distribution
    }
}

// Finds the single overall kVA peak for a circuit over a date range, with NO on/off
// split. Used only when the building has no operating hours configured - mirrors
// getPeakKvaBusinessHours' internal max-finding logic but skips the
// business-hours bucketing entirely, so there's no "off" bucket to come back empty
// (which is what made the original code bail out silently for these buildings).
const getOverallPeakKva = async (cancelToken, circuit, start, end) => {
    const res = await API.getElectricity(cancelToken, start, end, circuit.id, 15, 'kva')
    let maxKey = null
    let maxValue = 0
    const series = res.data.series
    for (const key in series) {
        if (series[key] > maxValue) {
            maxKey = key
            maxValue = series[key]
        }
    }
    return { [maxKey]: maxValue }
}

// Proportional dollar allocation for one half (on or off) of a circuit's usage,
// exactly reproducing the original inline math (including that `total` excludes
// base_dollar - that's how the original computed it everywhere, preserved as-is).
const allocatePortion = (halfKwh, totalKwh, priceTotal, monthlyPeakValue, isRealPeakHalf) => {
    const kvaBase = Object.values(priceTotal.kva_consumption).reduce((sum, val) => sum + (val ? val : 0), 0)
    const price = {
        kwh_consumption_dollar: (halfKwh / totalKwh) * priceTotal.kwh_consumption_dollar,
        base_dollar: (halfKwh / totalKwh) * priceTotal.base_dollar,
        carbon_charge_dollar: (halfKwh / totalKwh) * priceTotal.carbon_charge_dollar,
        kva_consumption_dollar: isRealPeakHalf ? (monthlyPeakValue / kvaBase) * priceTotal.kva_consumption_dollar : 0
    }

    let ratio = (price.kwh_consumption_dollar + price.carbon_charge_dollar + price.kva_consumption_dollar + price.base_dollar) /
        (priceTotal.kwh_consumption_dollar + priceTotal.carbon_charge_dollar + priceTotal.kva_consumption_dollar + priceTotal.base_dollar)
    if (isNaN(ratio)) ratio = 0

    price.municipal_tax_dollar = ratio * priceTotal.municipal_tax_dollar
    price.pst_dollar = ratio * priceTotal.pst_dollar
    price.gst_dollar = ratio * priceTotal.gst_dollar
    price.total = price.kwh_consumption_dollar + price.carbon_charge_dollar + price.kva_consumption_dollar + price.municipal_tax_dollar + price.pst_dollar + price.gst_dollar
    return price
}

// Full on/off proportional allocation for a circuit (used for both the primary
// period and the compare period's "NoSubt" / raw values).
const allocateProportional = (circuitKwh, totalKwh, priceTotal, monthlyPeaks, circuitName, realPeakOn) => ({
    on: allocatePortion(circuitKwh.on, totalKwh, priceTotal, monthlyPeaks[circuitName], realPeakOn),
    off: allocatePortion(circuitKwh.off, totalKwh, priceTotal, monthlyPeaks[circuitName], !realPeakOn)
})

const getPeakKvaBusinessHours = async (cancelToken, circuit, start, end, businessHours) => {
        let businessHoursNoMinutes = businessHours.map(e => e.split(":")[0])
        circuit = JSON.parse(JSON.stringify(circuit));
        let res = await API.getElectricity(cancelToken, start, end, circuit.id, 15, 'kva')
        let maxOnKey = null;
        let maxOnValue = 0;
        let maxOffKey = null;
        let maxOffValue = 0;
        let data = res.data.series

        for (const key in data) {
            if (businessHoursNoMinutes.includes(key.split(":")[0])) {
                if (data[key] > maxOnValue) {
                    maxOnKey = key;
                    maxOnValue = data[key];
                }
            } else {
                if (data[key] > maxOffValue) {
                    maxOffKey = key;
                    maxOffValue = data[key];
                }
            }
        }
        return {
            on: { [maxOnKey]: maxOnValue },
            off: { [maxOffKey]: maxOffValue }
        }
    }

// base - current for every dollar field of one half (on or off), the same
// subtraction the compare effect used to do inline. `base` may be missing (a
// circuit that didn't exist in the base period) - the original code assumed it
// always existed and would throw; this defaults to 0 instead, which is a small,
// deliberate hardening called out in the summary below.
const diffPortion = (baseHalf, currentHalf) => {
    const diff = {}
    for (const field of ['kwh_consumption_dollar', 'base_dollar', 'carbon_charge_dollar', 'kva_consumption_dollar', 'municipal_tax_dollar', 'pst_dollar', 'gst_dollar']) {
        diff[field] = (baseHalf ? baseHalf[field] : 0) - currentHalf[field]
    }
    diff.total = diff.kwh_consumption_dollar + diff.carbon_charge_dollar + diff.kva_consumption_dollar + diff.municipal_tax_dollar + diff.pst_dollar + diff.gst_dollar
    return diff
}

// Applies the circuit/category/sub-category/equipment-group/distribution filters
// used by the "finalCircuits" effect. Same logic that used to be duplicated for
// the compare and non-compare branches.
const filterCircuits = (circuitsDict, filters) => {
    const result = JSON.parse(JSON.stringify(circuitsDict))
    for (const key in result) {
        const c = result[key]
        if (filters.circuits.length > 0 && !filters.circuits.includes(key)) { delete result[key]; continue }
        if (filters.categories.length > 0 && !filters.categories.includes(c.category)) { delete result[key]; continue }
        if (filters.subCategories.length > 0 && !filters.subCategories.includes(c.subcategory)) { delete result[key]; continue }
        if (filters.equipmentGroups.length > 0 && !filters.equipmentGroups.includes(c.equipmentGroup)) { delete result[key]; continue }
        if (filters.distribution.length > 0 && !filters.distribution.includes(c.distribution)) { delete result[key]; continue }
    }
    return result
}

// Fetches hourly kw series for every generation source (skipping utility sources,
// same filter the original used), tagging each result with its channel_name.
// Shared by the "generation" view's primary and compare effects.
const fetchSourceSeries = async (cancelToken, sources, start, end) => {
    const promises = sources
        .map((circuit) => {
            // Snapshot channel_name defensively before the request resolves, same as the original.
            const tempCircuit = JSON.parse(JSON.stringify(circuit))
            if (!circuit || (circuit.category !== 'Building Distribution' && circuit.sub_category === 'Utility')) return null
            return API.getElectricity(cancelToken, start, end, circuit.id, 60, 'kw').then(res => {
                res.data.channel_name = tempCircuit.channel_name
                return res.data
            }).catch(e => {
                if (isCancel(e)) return null
                console.log(e)
                return null
            })
        })
        .filter(Boolean)
    return (await Promise.all(promises)).filter(Boolean)
}

const Reports = () => {

    const context = useContext(AppContext);
    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [mainCircuit, setMainCircuit] = useState(null)
    const [unit, setUnit] = useState('price')
    const [view, setView] = useState('circuit')

    const [openFilters, setOpenFilters] = useState(false)

    const [submit, setSubmit] = useState(false);
    const [filteredCircuits, setFilteredCircuits] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [filteredSubCategories, setFilteredSubCategories] = useState([]);
    const [filteredEquipmentGroups, setFilteredEquipmentGroups] = useState([]);
    const [filteredDistribution, setFilteredDistribution] = useState([]);
    const [finalCircuits, setFinalCircuits] = useState([])
    const [finalCircuitsNoComp, setFinalCircuitsNoComp] = useState([])
    const [showBuildings, setShowBuildings] = useState(false)
    // hasOpHours: does this building have an operating-hours schedule at all?
    // opHours: the business-hours list for the current primary period (only meaningful when hasOpHours is true).
    // opHoursResolved: has effect 1 finished figuring out the two values above for the current run?
    const [hasOpHours, setHasOpHours] = useState(true)
    const [opHours, setOpHours] = useState([])
    const [opHoursResolved, setOpHoursResolved] = useState(false)
    const [baseCircuits, setBaseCircuits] = useState(null)
    const [generationCircuits, setGenerationCircuits] = useState(null)
    const [generationFinal, setGenerationFinal] = useState(null)
    const [peakPf, setPeakPf] = useState(null)
    const [peakPfCompare, setPeakPfCompare] = useState(null)
    const [generationFinalNoSubt, setGenerationFinalNoSubt] = useState(null)
    const [categoriesOptions, setCategoriesOptions] = useState(null)
    const [subCategoriesOptions, setSubCategoriesOptions] = useState(null)
    const [equipmentGroupsOptions, setEquipmentGroupsOptions] = useState(null)
    const [buildingDistOptions, setBuildingDistOptions] = useState(null)
    const [compareCircuits, setCompareCircuits] = useState(null)
    const [compareCircuitsNoSubt, setCompareCircuitsNoSubt] = useState(null)
    const [virtualTotal, setVirtualTotal] = useState(null)
    const [virtualTotalCompare, setVirtualTotalCompare] = useState(null)
    const [realPeak, setRealPeak] = useState(null)
    const [realPeakCompare, setRealPeakCompare] = useState(null)
    const [totalPrice, setTotalPrice] = useState(null)
    const [totalPriceCompare, setTotalPriceCompare] = useState(null)
    const [totalPriceCompareNoDiff, setTotalPriceCompareNoDiff] = useState(null)
    const [totalCo, setTotalCo] = useState(null)
    const [totalCoCompare, setTotalCoCompare] = useState(null)
    const [complete, setComplete] = useState("")
    const [settings, setSettings] = useState({ startEnd: null, compareOn: false, compareStartEnd: null })

    const [tempSettings, setTempSettings] = useState({ startEnd: [moment().subtract(1, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')], compareOn: false, compareStartEnd: null })
    const navigate = useNavigate();

    // ---- Effect 1: resolve the main circuit + whether/what the operating hours are ----
    useEffect(() => {
        if (!context.getBuildingInfo || !settings.startEnd || !submit) return

        const cancelToken = API.cancelToken();
        setOpHoursResolved(false)

        if (context.getBuildingInfo.has_operating_hours) {
            API.getOpHoursRange(cancelToken, context.getBuilding, settings.startEnd[0], settings.startEnd[1]).then(res => {
                setOpHours(res.data)
                setHasOpHours(true)
                setOpHoursResolved(true)
            }).catch(e => {
                if (isCancel(e)) return
                console.log("Failed to get Op hours")
                console.log(e);
            })
        } else {
            // No operating-hours schedule for this building: there is no on/off split to make.
            // Every effect below checks `hasOpHours` and, when false, treats the whole period as
            // "on" and leaves "off" at zero instead of trying to bucket by business hours.
            setOpHours([])
            setHasOpHours(false)
            setOpHoursResolved(true)
        }

        let end_circuits = GraphFuncs.findCirctuitInHierarchy('Main Distribution', context.getBuildingInfo.hierarchy)
        end_circuits.type = 'electricity'
        setMainCircuit(end_circuits)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context.getBuildingInfo, settings.startEnd, submit]);

    // ---- Effect 2: seed tempSettings from the URL query params ----
    useEffect(() => {
        let newBuilding = queryParams.get('building')
        let start = queryParams.get('start')
        if (!start) start = moment().subtract(1, 'months').format('YYYY-MM-DD')
        let end = queryParams.get('end')
        if (!end) end = moment().format('YYYY-MM-DD')

        if (!context.getUserInfo) return
        if (queryParams.size !== 0) {
            let building = context.getUserInfo.buildings.find(e => e.building_id === newBuilding)
            if (building && building.building !== tempSettings.building) {
                setTempSettings(prev => ({ ...prev, building: building.building, compareAllowed: false, compareOn: false, startEnd: [start, end] }))
                if (newBuilding !== context.getBuilding) setShowBuildings(false)
            } else {
                setTempSettings(prev => ({ ...prev, startEnd: [start, end] }))
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams, context.getUserInfo])

    // ---- Effect 3: primary-period data (baseCircuits) ----
    useEffect(() => {
        if (!mainCircuit || !opHoursResolved) return

        const cancelToken = API.cancelToken();

        const loadPrimaryReportData = async () => {
            try {
                let peakOn, onHoursData, offHoursData, realPeakOn, realPeakValue

                if (hasOpHours) {
                    const peaksDict = await getPeakKvaBusinessHours(cancelToken, mainCircuit, settings.startEnd[0], settings.startEnd[1], opHours)
                    const peakOnKey = Object.keys(peaksDict.on)[0]
                    const peakOffKey = Object.keys(peaksDict.off)[0]
                    if ((!peakOnKey || peakOnKey === "null") || (!peakOffKey || peakOffKey === "null")) return

                    onHoursData = (await API.getPeakBatch(cancelToken, context.getBuilding, peakOnKey)).data
                    offHoursData = (await API.getPeakBatch(cancelToken, context.getBuilding, peakOffKey)).data

                    realPeakOn = peaksDict.on[peakOnKey] > peaksDict.off[peakOffKey]
                    peakOn = realPeakOn ? peakOnKey : peakOffKey
                    realPeakValue = realPeakOn ? { [peakOnKey]: peaksDict.on[peakOnKey] } : { [peakOffKey]: peaksDict.off[peakOffKey] }
                } else {
                    // No operating hours: find the single overall peak, there is no "off" bucket to fetch.
                    realPeakValue = await getOverallPeakKva(cancelToken, mainCircuit, settings.startEnd[0], settings.startEnd[1])
                    peakOn = Object.keys(realPeakValue)[0]
                    if (!peakOn || peakOn === "null") return

                    realPeakOn = true
                    onHoursData = (await API.getPeakBatch(cancelToken, context.getBuilding, peakOn)).data
                    offHoursData = {}
                }

                setRealPeak(realPeakValue)

                let co = await API.getPriceCoElectric(cancelToken, settings.startEnd[0], settings.startEnd[1], mainCircuit.id, 60)
                co = Object.values(co.data.series).reduce((sum, value) => (sum + value), 0)
                setTotalCo(co)

                let priceTotal = await API.getPriceE(cancelToken, settings.startEnd[0], settings.startEnd[1], mainCircuit.id)
                priceTotal = priceTotal.data
                setTotalPrice(priceTotal.total)

                let monthlyPeaks = await API.getPeakBatchMonthly(cancelToken, context.getBuilding, Object.keys(priceTotal.kva_consumption).join(","))
                monthlyPeaks = monthlyPeaks.data

                let pfDict = await API.getPeakBatch(cancelToken, context.getBuilding, peakOn, 'pf')
                pfDict = pfDict.data

                let kwh = await API.getKwhBatch(cancelToken, context.getBuilding, settings.startEnd[0], settings.startEnd[1])
                kwh = kwh.data
                Object.keys(kwh).forEach(key => {
                    if (key.includes("Cap Bank")) delete kwh[key]
                })
                let totalKwh = Object.values(kwh).reduce((sum, value) => (sum + value.on + value.off), 0)
                setVirtualTotal(totalKwh.toFixed(2))

                let tempCircuitDict = {}
                let categories = new Set()
                let subcategories = new Set()
                let buildingDists = new Set()
                let equipmentGroups = new Set()

                for (let c in onHoursData) {
                    const channelInfo = context.getBuildingInfo.e_channels.filter(e => e.channel_name.trim() === c.trim())
                    const meta = extractChannelMeta(channelInfo)

                    let circuitKwh = c in kwh ? kwh[c] : { on: 0, off: 0 }
                    if (!hasOpHours) circuitKwh = { on: (circuitKwh.on || 0) + (circuitKwh.off || 0), off: 0 }

                    const price = allocateProportional(circuitKwh, totalKwh, priceTotal, monthlyPeaks, c, realPeakOn)

                    buildingDists.add(meta.distribution)
                    categories.add(meta.category)
                    subcategories.add(meta.subcategory)
                    equipmentGroups.add(meta.equipmentGroup)

                    tempCircuitDict[c] = {
                        kva: {
                            on: onHoursData[c],
                            off: c in offHoursData ? offHoursData[c] : 0
                        },
                        kwh: circuitKwh,
                        price,
                        co: (circuitKwh.on / totalKwh) * co + (circuitKwh.off / totalKwh) * co,
                        pf: pfDict[c],
                        realPeakOn,
                        category: meta.category,
                        subcategory: meta.subcategory,
                        equipmentGroup: meta.equipmentGroup,
                        distribution: meta.distribution
                    }
                }

                const categoriesArr = Array.from(categories).filter(item => !['Source', 'Building Distribution', null].includes(item)).sort()
                const subcategoriesArr = Array.from(subcategories).filter(item => !['Source', 'Building Distribution'].includes(item)).sort()
                const equipmentGroupsArr = Array.from(equipmentGroups)

                setBaseCircuits(tempCircuitDict)
                setFilteredCircuits(Object.keys(onHoursData))
                setFilteredCategories(categoriesArr)
                setCategoriesOptions(categoriesArr)
                setFilteredSubCategories(subcategoriesArr)
                setSubCategoriesOptions(subcategoriesArr)
                setFilteredEquipmentGroups(equipmentGroupsArr)
                setEquipmentGroupsOptions(equipmentGroupsArr)
                setFilteredDistribution(Array.from(buildingDists))
                setBuildingDistOptions(Array.from(buildingDists))
                setPeakPf(pfDict[mainCircuit.channel_name])
            } catch (e) {
                console.log(e)
                if (isCancel(e)) return
                showGenericError()
            }
        }

        loadPrimaryReportData()

        return () => {
            API.cancel(cancelToken);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainCircuit, opHoursResolved, hasOpHours, opHours])

    // ---- Effect 4: compare-period data (compareCircuits) ----
    useEffect(() => {
        if (!baseCircuits || !settings.compareOn || !settings.compareStartEnd || !context.getBuildingInfo) return

        const cancelToken = API.cancelToken();

        const loadCompareReportData = async () => {
            try {
                let peakOn, onHoursData, offHoursData, realPeakOn, realPeakValue

                if (hasOpHours) {
                    const opHoursRes = await API.getOpHoursRange(cancelToken, context.getBuilding, settings.compareStartEnd[0], settings.compareStartEnd[1])
                    const peaksDict = await getPeakKvaBusinessHours(cancelToken, mainCircuit, settings.compareStartEnd[0], settings.compareStartEnd[1], opHoursRes.data)
                    const peakOnKey = Object.keys(peaksDict.on)[0]
                    const peakOffKey = Object.keys(peaksDict.off)[0]
                    if ((!peakOnKey || peakOnKey === "null") || (!peakOffKey || peakOffKey === "null")) return

                    onHoursData = (await API.getPeakBatch(cancelToken, context.getBuilding, peakOnKey)).data
                    offHoursData = (await API.getPeakBatch(cancelToken, context.getBuilding, peakOffKey)).data

                    realPeakOn = peaksDict.on[peakOnKey] > peaksDict.off[peakOffKey]
                    peakOn = realPeakOn ? peakOnKey : peakOffKey
                    realPeakValue = realPeakOn ? { [peakOnKey]: peaksDict.on[peakOnKey] } : { [peakOffKey]: peaksDict.off[peakOffKey] }
                } else {
                    realPeakValue = await getOverallPeakKva(cancelToken, mainCircuit, settings.compareStartEnd[0], settings.compareStartEnd[1])
                    peakOn = Object.keys(realPeakValue)[0]
                    if (!peakOn || peakOn === "null") return

                    realPeakOn = true
                    onHoursData = (await API.getPeakBatch(cancelToken, context.getBuilding, peakOn)).data
                    offHoursData = {}
                }

                setRealPeakCompare(realPeakValue)

                let co = await API.getPriceCoElectric(cancelToken, settings.compareStartEnd[0], settings.compareStartEnd[1], mainCircuit.id, 60)
                co = Object.values(co.data.series).reduce((sum, value) => (sum + value), 0)
                setTotalCoCompare(co)

                let priceTotal = await API.getPriceE(cancelToken, settings.compareStartEnd[0], settings.compareStartEnd[1], mainCircuit.id)
                priceTotal = priceTotal.data
                setTotalPriceCompare(totalPrice - priceTotal.total)
                setTotalPriceCompareNoDiff(priceTotal.total)

                let monthlyPeaks = await API.getPeakBatchMonthly(cancelToken, context.getBuilding, Object.keys(priceTotal.kva_consumption).join(","))
                monthlyPeaks = monthlyPeaks.data

                let pfDict = await API.getPeakBatch(cancelToken, context.getBuilding, peakOn, 'pf')
                pfDict = pfDict.data

                let kwh = await API.getKwhBatch(cancelToken, context.getBuilding, settings.compareStartEnd[0], settings.compareStartEnd[1])
                kwh = kwh.data
                Object.keys(kwh).forEach(key => {
                    if (key.includes("Cap Bank")) delete kwh[key]
                })
                let totalKwh = Object.values(kwh).reduce((sum, value) => (sum + value.on + value.off), 0)
                setVirtualTotalCompare((virtualTotal - totalKwh).toFixed(2))

                let tempCircuitDict = {}
                let tempCircuitDictNoSubt = {}

                for (let c in onHoursData) {
                    let circuitKwh = c in kwh ? kwh[c] : { on: 0, off: 0 }
                    if (!hasOpHours) circuitKwh = { on: (circuitKwh.on || 0) + (circuitKwh.off || 0), off: 0 }

                    const priceNoSubt = allocateProportional(circuitKwh, totalKwh, priceTotal, monthlyPeaks, c, realPeakOn)
                    const base = c in baseCircuits ? baseCircuits[c] : null
                    const price = {
                        on: diffPortion(base && base.price.on, priceNoSubt.on),
                        off: diffPortion(base && base.price.off, priceNoSubt.off)
                    }

                    const channelInfo = context.getBuildingInfo.e_channels.filter(e => e.channel_name.trim() === c.trim())
                    const meta = extractChannelMeta(channelInfo)
                    const circuitCo = (circuitKwh.on / totalKwh) * co + (circuitKwh.off / totalKwh) * co

                    tempCircuitDict[c] = {
                        kva: {
                            on: (base ? base.kva.on : 0) - onHoursData[c],
                            off: (base ? base.kva.off : 0) - (c in offHoursData ? offHoursData[c] : 0)
                        },
                        kwh: {
                            on: (base ? base.kwh.on : 0) - circuitKwh.on,
                            off: (base ? base.kwh.off : 0) - circuitKwh.off
                        },
                        price,
                        co: (base ? base.co : 0) - circuitCo,
                        pf: (base ? base.pf : 0) - pfDict[c],
                        realPeakOn,
                        category: meta.category,
                        subcategory: meta.subcategory,
                        equipmentGroup: meta.equipmentGroup,
                        distribution: meta.distribution
                    }

                    tempCircuitDictNoSubt[c] = {
                        kva: {
                            on: onHoursData[c],
                            off: c in offHoursData ? offHoursData[c] : 0
                        },
                        kwh: { on: circuitKwh.on, off: circuitKwh.off },
                        price: priceNoSubt,
                        co: circuitCo,
                        pf: pfDict[c],
                        realPeakOn,
                        category: meta.category,
                        distribution: meta.distribution
                    }
                }

                setCompareCircuitsNoSubt(tempCircuitDictNoSubt)
                setCompareCircuits(tempCircuitDict)
                setPeakPfCompare(pfDict[mainCircuit.channel_name])
            } catch (e) {
                console.log(e)
                if (isCancel(e)) return
                showGenericError()
            }
        }

        loadCompareReportData()

        return () => {
            API.cancel(cancelToken);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseCircuits, settings.compareOn, settings.compareStartEnd, mainCircuit, hasOpHours])

    // ---- Effect 5: apply circuit/category/etc filters to produce finalCircuits ----
    useEffect(() => {
        if (!baseCircuits) return
        if (settings.compareOn && !compareCircuits) return

        const filters = {
            circuits: filteredCircuits,
            categories: filteredCategories,
            subCategories: filteredSubCategories,
            equipmentGroups: filteredEquipmentGroups,
            distribution: filteredDistribution
        }

        const tempFilteredNoComp = filterCircuits(baseCircuits, filters)
        setFinalCircuitsNoComp(tempFilteredNoComp)

        if (settings.compareOn) {
            setFinalCircuits(filterCircuits(compareCircuits, filters))
        } else {
            setFinalCircuits(tempFilteredNoComp)
        }
    }, [baseCircuits, compareCircuits, filteredCircuits, filteredCategories, filteredSubCategories, filteredEquipmentGroups, filteredDistribution, settings.compareOn])

    // ---- Effect 6: generation view, primary period ----
    useEffect(() => {
        if (view !== 'generation') return

        const sources = [...context.getBuildingInfo.e_sources, mainCircuit]
        const cancelToken = API.cancelToken();

        fetchSourceSeries(cancelToken, sources, settings.startEnd[0], settings.startEnd[1]).then(sourceSeries => {
            const businessHoursNoMinutes = opHours.map(e => e.split(":")[0])
            const tempSources = {}
            for (const source of sourceSeries) {
                tempSources[source.channel_name] = { kwh: { on: 0, off: 0 } }
                for (const timestamp in source.series) {
                    const isOn = hasOpHours ? businessHoursNoMinutes.includes(timestamp.split(":")[0]) : true
                    tempSources[source.channel_name].kwh[isOn ? 'on' : 'off'] += source.series[timestamp]
                }
            }
            setGenerationCircuits(tempSources)
            setGenerationFinal(tempSources)
        })

        return () => {
            API.cancel(cancelToken);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, context.getBuildingInfo, hasOpHours])

    // ---- Effect 7: generation view, compare period ----
    useEffect(() => {
        if (!generationCircuits) return
        const cancelToken = API.cancelToken();

        if (!settings.compareOn) {
            setGenerationFinal(generationCircuits)
            return () => API.cancel(cancelToken)
        }
        if (view !== 'generation' || !settings.compareStartEnd) return () => API.cancel(cancelToken)

        const sources = [...context.getBuildingInfo.e_sources, mainCircuit]

        const loadCompareGeneration = async () => {
            const sourceSeries = await fetchSourceSeries(cancelToken, sources, settings.compareStartEnd[0], settings.compareStartEnd[1])

            let businessHoursNoMinutes = []
            if (hasOpHours) {
                const opHoursRes = await API.getOpHoursRange(cancelToken, context.getBuilding, settings.compareStartEnd[0], settings.compareStartEnd[1])
                businessHoursNoMinutes = opHoursRes.data.map(e => e.split(":")[0])
            }

            const tempSources = {}
            const tempSourcesNoComp = {}
            for (const source of sourceSeries) {
                tempSources[source.channel_name] = { kwh: { on: 0, off: 0 } }
                tempSourcesNoComp[source.channel_name] = { kwh: { on: 0, off: 0 } }
                for (const timestamp in source.series) {
                    const isOn = hasOpHours ? businessHoursNoMinutes.includes(timestamp.split(":")[0]) : true
                    const bucket = isOn ? 'on' : 'off'
                    tempSources[source.channel_name].kwh[bucket] += source.series[timestamp]
                    tempSourcesNoComp[source.channel_name].kwh[bucket] += source.series[timestamp]
                }
            }

            // NOTE: preserved from the original - tempSourcesNoComp ends up mirroring the diffed
            // tempSources values below (it reads tempSources *after* the diff is applied), so despite
            // the name it is not actually the raw compare-period total. Flagged in the summary; left
            // as-is here since fixing it would change what the UI shows.
            for (const source in tempSources) {
                tempSources[source].kwh.on = generationCircuits[source].kwh.on - tempSources[source].kwh.on
                tempSourcesNoComp[source].kwh.on = tempSources[source].kwh.on
                tempSources[source].kwh.off = generationCircuits[source].kwh.off - tempSources[source].kwh.off
                tempSourcesNoComp[source].kwh.off = tempSources[source].kwh.off
            }

            setGenerationFinal(tempSources)
            setGenerationFinalNoSubt(tempSourcesNoComp)
        }

        loadCompareGeneration().catch(e => {
            console.log(e)
            if (isCancel(e)) return
            showGenericError()
        })

        return () => {
            API.cancel(cancelToken);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, context.getBuildingInfo, settings.compareStartEnd, settings.compareOn, generationCircuits, hasOpHours])

    const handleDateChange = (data) => {
        if (!data.value) return
        let startDate = data.value[0]
        let endDate = data.value[1]
        navigate(`/reports?building=${queryParams.get('building')}&start=${GlobalFuncs.formatDate(startDate, 'YYYY-MM-DD')}&end=${GlobalFuncs.formatDate(endDate, 'YYYY-MM-DD')}`)
    }

    const handleDateCompare = (data) => {
        if (!data.value) return
        let diff = GraphFuncs.getDifferenceInDays(settings.startEnd[0], settings.startEnd[1]) - 1
        let startDate = data.value
        let endDate = moment(startDate).add(diff, 'days');
        setTempSettings(prev => ({ ...prev, compareStartEnd: [moment(startDate).format("YYYY-MM-DD"), moment(endDate).format("YYYY-MM-DD")] }))
    }

    const handleCompareOff = () => {
        if (!settings.compareOn && !tempSettings.compareOn) return
        setTempSettings(prev => ({ ...prev, compareOn: false }))
        setSettings(prev => ({ ...prev, compareOn: false }))
    }

    const handleChangeUnit = (e) => {
        setUnit(e.currentTarget.value)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setComplete("")
        const newBuilding = queryParams.get('building')
        if (queryParams.size === 0) return

        const compareAllowed = ['1', '2', '3'].every(item => complete.includes(item))

        if (newBuilding !== context.getBuilding) {
            setShowBuildings(false)
            context.setBuilding(newBuilding)
            setTempSettings(prev => ({ ...prev, compareAllowed, compareOn: false, compareStartEnd: null }))
            setSettings(prev => ({ ...prev, compareAllowed, compareOn: false, compareStartEnd: null, startEnd: tempSettings.startEnd }))
        } else {
            setTempSettings(prev => ({ ...prev, compareAllowed, compareOn: tempSettings.compareOn }))
            setSettings(prev => ({ ...prev, compareAllowed, compareOn: tempSettings.compareOn, compareStartEnd: tempSettings.compareStartEnd ? tempSettings.compareStartEnd : null, startEnd: tempSettings.startEnd }))
        }
        setSubmit(true)
    }

    // ---- Effect 8: once all report sections report themselves complete, allow a new submit ----
    useEffect(() => {
        if (!['1', '2', '3'].every(item => complete.includes(item))) return
        setSubmit(false)
        setTempSettings(prev => ({ ...prev, compareAllowed: true }))
        setSettings(prev => ({ ...prev, compareAllowed: true }))
    }, [complete])

    return (
        <div>
            <div className="laptop:px-8 tablet:px-4 mobile:px-4 py-2 flex flex-nowrap justify-between items-center rounded-md bg-[#FFF] shadow-md" >
                <div className='flex flex-nowrap gap-6 text-base font-bold'>
                    <Link to={`/reports?building=all`} className='active-reports-tab'>Buildings</Link>
                    <Link to={`/report?building=${queryParams.get('building')}`} className=''>Portfolio</Link>
                    <Link to={`/billreports`} className=''>Bills</Link>
                </div>
                <div className='flex flex-nowrap justify-end items-center w-9/12'>
                    {!context.getLaptop &&
                        <div className="flex flex-norap justify-end items-center">
                            <div className='flex justify-end items-center m-2'>
                                <DateRangePicker onChange={handleDateChange}
                                    start={tempSettings.startEnd[0]}
                                    end={tempSettings.startEnd[1]}
                                    compare={tempSettings.compareOn}
                                    onChangeCompare={handleDateCompare}
                                    compareStart={tempSettings.compareStartEnd} />
                            </div>
                            {!tempSettings.compareOn && tempSettings.compareAllowed && baseCircuits && <div className='flex flex-nowrap justify-center items-center m-2 cursor-pointer' onClick={() => setTempSettings(prev => ({ ...prev, compareOn: true }))}>
                                {GlobalSVG.pentagon()}
                                <p className='ml-2 font-medium text-[#59595C]'>Compare Periods</p>
                            </div>}
                            {tempSettings.compareOn && tempSettings.compareAllowed && <div className='flex flex-nowrap justify-center items-center m-2 cursor-pointer' onClick={handleCompareOff}>
                                {GlobalSVG.closeBig()}
                                <p className='ml-2 font-medium text-[#59595C]'>Stop Comparison</p>
                            </div>}
                        </div>
                    }
                    {!context.getLaptop &&
                        <div className="relative flex flex-nowrap justify-center items-center w-3/12">
                            <div onClick={() => setShowBuildings(!showBuildings)} className='p-1 border border-2 rounded-[8px] transparent  border-[#E5E7EB] h-[43px] cursor-pointer flex flex-nowrap justify-between items-center w-10/12'>
                                <p className='font-sans font-normal text-sans text-[#C1C1C1]'>{tempSettings.building ? tempSettings.building : "Select building"}</p>
                                {showBuildings && GlobalSVG.sortArrowDesc('#C1C1C1')}
                                {!showBuildings && GlobalSVG.sortArrowAsc('#C1C1C1')}
                            </div>
                            <div className={`${showBuildings ? 'block' : 'hidden'} z-10 h-[300px] absolute laptop:top-[160%] left-0 tablet:top-[100%] mobile:top-[100%] w-full flex items-start justify-center `}>
                                <div className='card buildings-card w-full laptop:h-full tablet:h-full mobile:h-full p-2 right-0'>
                                    <Accordion onSelect={() => null} compact={true} linkTo='reports' />
                                </div>
                            </div>
                        </div>
                    }
                    {!context.getLaptop && tempSettings.startEnd && tempSettings.building && tempSettings.building !== 'all' && !submit &&
                        <button onClick={handleSubmit} className='submit-button'>Generate Report</button>
                    }
                </div>
                {
                    context.getLaptop &&
                    <div className="input-group-text flex items-center whitespace-nowrap cursor-pointer" onClick={() => setOpenFilters(!openFilters)}>
                        {GlobalSVG.menuExport()}
                        <p className="text-sm text-normal leading-4">Settings</p>
                    </div>
                }
            </div >

            {
                context.getLaptop && openFilters &&
                <div className={`${openFilters ? 'tablet:w-[300px] mobile:w-full p-3' : "w-0"} card alert-sidebar transition-width duration-300 ease-in-out h-full fixed right-0 tablet:top-[10] mobile:top-[10] z-40 background-white`}>
                    <div className="mt-8 relative flex flex-nowrap justify-center items-center w-full gap-3">
                        <div onClick={() => setShowBuildings(!showBuildings)} className='p-1 border border-2 rounded-[8px] transparent  border-[#E5E7EB] h-[43px] cursor-pointer flex flex-nowrap justify-between items-center w-10/12'>
                            <p className='font-sans font-normal text-sans text-[#C1C1C1]'>{tempSettings.building ? tempSettings.building : "Select building"}</p>
                            {GlobalSVG.sortArrowDesc('#C1C1C1')}
                        </div>
                        {showBuildings &&
                            <div className='z-10 h-[300px] absolute laptop:top-[130%] left-0 tablet:top-[100%] mobile:top-[100%] w-parent flex items-start justify-center '>
                                <div className='card buildings-card w-full laptop:h-full tablet:h-full mobile:h-full p-2 right-0'>
                                    <Accordion onSelect={(e) => console.log(e)} compact={true} linkTo='reports' />
                                </div>
                            </div>
                        }
                    </div>
                    <div className='w-full mt-8'>
                        <DateRangePicker onChange={handleDateChange}
                            start={tempSettings.startEnd[0]}
                            end={tempSettings.startEnd[1]}
                            compare={tempSettings.compareOn}
                            onChangeCompare={handleDateCompare}
                            compareStart={tempSettings.compareStartEnd}
                            wrap={true} />
                    </div>
                    {!tempSettings.compareOn && baseCircuits &&
                        <div className='flex flex-nowrap justify-center items-center m-2 mb-8 cursor-pointer' onClick={() => setTempSettings(prev => ({ ...prev, compareOn: true }))}>
                            {GlobalSVG.pentagon()}
                            <p className='ml-2 font-medium text-[#59595C]'>Compare Periods</p>
                        </div>}
                    {tempSettings.compareOn && <div className='flex flex-nowrap justify-center items-center m-2 mt-4 mb-8 cursor-pointer' onClick={handleCompareOff}>
                        {GlobalSVG.closeBig()}
                        <p className='ml-2 font-medium text-[#59595C]'>Stop Comparison</p>
                    </div>}
                    {tempSettings.startEnd[0] && tempSettings.building && tempSettings.building !== 'all' && !submit &&
                        <button onClick={handleSubmit} className='submit-button mx-auto mt-8'>Generate Report</button>
                    }
                </div>
            }

            {/* Report body only renders once sections 1-3 have reported themselves ready.
                (A 4th section, RuntimeGraph, used to be part of this gate - it's currently disabled;
                see the commented-out block below "BreakerGraph" to re-enable it.) */}
            <div className={`tablet:p-12 mobile:p-3 ${complete && ['1', '2', '3'].every(item => complete.includes(item)) ? 'block' : 'hidden'}`}>
                {baseCircuits && finalCircuits &&
                    context.getBuildingInfo &&
                    <h2 className='laptop:text-3xl tablet:text-xl font-bold text-[#332D41]'>{context.getBuildingInfo.building}</h2>
                }
                {baseCircuits &&
                    <div className='flex flex-wrap laptop:flex-row mobile:flex-col justify-left gap-x-6'>
                        <div className='card laptop:w-[49%] tablet:w-full mobile:w-full min-w-[200px] tablet:p-8 mobile:p-2 items-center rounded-lg p-6 mt-8'>
                            <ReportPie1
                                circuits={baseCircuits}
                                generationCircuits={generationFinal}
                                view={view} filter={setFilteredCategories}
                                unit={unit}
                                totalPrice={totalPrice}
                                realPeak={realPeak} />
                        </div>
                        {compareCircuits && <div className='card laptop:w-[49%] tablet:w-full mobile:w-full min-w-[200px] tablet:p-8 mobile:p-2 items-center rounded-lg p-6 mt-8'>
                            <ReportPie1
                                circuits={compareCircuitsNoSubt}
                                generationCircuits={generationFinalNoSubt}
                                view={view}
                                filter={setFilteredCategories}
                                unit={unit}
                                compare={true}
                                totalPrice={totalPriceCompareNoDiff}
                                realPeak={realPeakCompare} />
                        </div>}
                        <div className='card laptop:w-[49%] tablet:w-full mobile:w-full min-w-[200px] tablet:p-8 mobile:p-2 items-center rounded-lg p-6 mt-8'>
                            <ReportPie2 circuits={finalCircuitsNoComp} totalPrice={totalPrice} />
                        </div>
                        {compareCircuits && <div className='card laptop:w-[49%] tablet:w-full mobile:w-full min-w-[200px] tablet:p-8 mobile:p-2 items-center rounded-lg p-6 mt-8'>
                            <ReportPie2 circuits={compareCircuitsNoSubt} compare={true} totalPrice={totalPriceCompareNoDiff} />
                        </div>}

                    </div>
                }
                {baseCircuits && finalCircuits &&
                    <ReportGraph1
                        setComplete={setComplete}
                        circuits={finalCircuits}
                        circuitOptions={Object.keys(baseCircuits)}
                        categoryOptions={categoriesOptions}
                        subCategoryOptions={subCategoriesOptions}
                        equipmentGroupsOptions={equipmentGroupsOptions}
                        distributionOptions={buildingDistOptions}
                        unit={unit}
                        startEnd={settings.startEnd}
                        setUnit={handleChangeUnit}
                        selectCircuit={setFilteredCircuits}
                        selectCategory={setFilteredCategories}
                        selectSubCategory={setFilteredSubCategories}
                        selectEquipmentGroup={setFilteredEquipmentGroups}
                        selectDistribution={setFilteredDistribution}
                        selectedDistributions={filteredDistribution}
                        selectedCircuits={filteredCircuits}
                        selectedCategories={filteredCategories}
                        selectedSubCategories={filteredSubCategories}
                        selectedEquipmentGroups={filteredEquipmentGroups}
                        view={view}
                        realPeak={realPeak}
                        realPeakCompare={realPeakCompare}
                        totalPrice={totalPrice}
                        totalPriceCompare={totalPriceCompare}
                        setView={setView}
                        virtualTotal={virtualTotal}
                        totalCo={totalCo}
                        totalCoCompare={totalCoCompare}
                        virtualTotalCompare={virtualTotalCompare}
                        generationCircuits={generationFinal}
                        peakPf={peakPf}
                        peakPfCompare={peakPfCompare}
                        compareStartEnd={settings.compareStartEnd}
                        compare={settings.compareOn} />
                }
                {baseCircuits && finalCircuits &&
                    <ReportGraph2
                        setComplete={setComplete}
                        startEnd={settings.startEnd}
                        compareStartEnd={settings.compareStartEnd}
                        view={view}
                        virtualTotal={virtualTotal}
                        virtualTotalCompare={virtualTotalCompare}
                        realPeak={realPeak}
                        totalCoCompare={totalCoCompare}
                        totalCo={totalCo}
                        realPeakCompare={realPeakCompare}
                        setView={setView}
                        compare={settings.compareOn} />
                }
                {baseCircuits && finalCircuits &&
                    <div className='flex laptop:flex-col mobile:flex-col justify-left gap-x-6'>
                        <div className='card laptop:w-full tablet:w-full min-w-[300px] tablet:p-8 mobile:p-2 items-center rounded-lg p-6 mt-8'>
                            <BreakerGraph
                                setComplete={setComplete}
                                startEnd={settings.startEnd}
                                compare={settings.compareOn}
                                compareStartEnd={settings.compareStartEnd}
                                realPeak={realPeak}
                                realPeakCompare={realPeakCompare} />
                        </div>
                        {/* RuntimeGraph is disabled - re-enable by restoring its import, uncommenting the
                            block below, and bumping the completeness checks above (and in handleSubmit /
                            effect 8) from ['1','2','3'] back to ['1','2','3','4']. */}
                        {/* <div className='card laptop:w-full tablet:w-full min-w-[300px] tablet:p-8 mt-8 mobile:p-2 items-center rounded-lg p-6'>
                            <RuntimeGraph
                                setComplete={setComplete}
                                startEnd={settings.startEnd}
                                compare={settings.compareOn}
                                compareStartEnd={settings.compareStartEnd}
                            />
                        </div> */}
                    </div>
                }
            </div>
            {!submit && !['1', '2', '3'].every(item => complete.includes(item)) &&
                <div className='h-[70vh]'>
                    <p className='p-8 text-center'>Please select a building and a date range to begin.</p>
                </div>
            }
            {context.getBuilding && context.getBuilding !== 'all' && submit && !['1', '2', '3', '4'].every(item => complete.includes(item)) &&
                <div className='h-[70vh]'>
                    <p className='pt-8 text-center text-[#C1C1C1]'>Generating your report. This might take a few minutes...</p>
                    <Loader />
                </div>
            }
        </div>
    );
};

export default Reports;
